import React, { useState, useRef, useEffect, useCallback } from "react";
import { debounce } from "../../../Helpers/Performance/Performance";

interface ExpandableTextProps {
    children: React.ReactNode;
    collapsedHeight?: number;
    resizeDebounceMs?: number;
    className?: string;
    moreLabel?: string;
    lessLabel?: string;
    isLoading?: boolean;
}

const ExpandableText: React.FC<ExpandableTextProps> = (
    {
        children,
        collapsedHeight = 50,
        resizeDebounceMs = 100,
        className = "",
        moreLabel = "...More",
        lessLabel = "Show Less",
        isLoading
    }
) => {
    const
        [isExpanded, setIsExpanded] = useState(false),
        [containerHeight, setContainerHeight] = useState<number | null>(null),
        [isOverflowing, setIsOverflowing] = useState(false),
        contentRef = useRef<HTMLDivElement>(null),
        containerRef = useRef<HTMLDivElement>(null),
        calculate = useCallback(() => {
            const
                textEl = contentRef.current,
                containerEl = containerRef.current;

            if (!textEl || !containerEl) return;

            const fullHeight = textEl.scrollHeight;

            // Do we need to show “…More”?
            setIsOverflowing(fullHeight > collapsedHeight);

            // What height should we actually apply?
            setContainerHeight(isExpanded ? fullHeight : collapsedHeight);
        }, [isExpanded, collapsedHeight]);

    // Initial + whenever content or expanded state changes
    useEffect(() => {
        calculate();
    }, [children, isExpanded, calculate]);

    // Recalculate on window resize
    useEffect(() => {
        const onResize = debounce(calculate, resizeDebounceMs);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [calculate, resizeDebounceMs]);

    const toggle = () => setIsExpanded(exp => !exp);

    return (
        <div
            ref={containerRef}
            className={
                `layout layout--expandable-content-box u-relative_hidden
                ${className}
                ${isLoading !== true && children && React.Children.count(children) > 0 ? 'has-content' : 'has-no-content'}`
            }
            style={{
                height: containerHeight !== null ? `${containerHeight + (isOverflowing ? (isExpanded ? 75 : 65) : 32)}px` : 'auto',
                overflow: "hidden",
                transition: "height 0.3s ease",
                position: "relative",
            }}
        >
            <div className="expandable-content-box__inner" ref={contentRef}>
                {
                    isLoading === true ?
                        <div className="loader--side-to-side" />
                        :
                        children
                }
            </div>

            {isOverflowing && (
                <>
                    <div className="expandable-content-box__gradient-shield" />

                    <button
                        className="ickonic-button expandable-text__toggle"
                        onClick={toggle}
                    >
                        {isExpanded ? lessLabel : moreLabel}
                    </button>
                </>
            )}
        </div>
    );
};

export default ExpandableText;
